import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  type User as FirebaseUser,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile,
} from 'firebase/auth';
import type { Role } from '@portal/shared';
import { firebase } from './firebase.js';
import { api, ApiError } from './api-client.js';

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  createdAt: string;
  updatedAt: string;
}

export interface AuthContextValue {
  user: FirebaseUser | null;
  /** Portal user record with role, fetched via /api/me after auth. */
  me: PortalUser | null;
  loading: boolean;
  /** True when /api/me returned 403 ACCESS_DENIED — user has been signed out. */
  accessDenied: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [me, setMe] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const { auth } = firebase();
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setMe(null);
        setLoading(false);
        return;
      }
      // Keep loading=true while /api/me is in flight so RequireAuth shows the
      // spinner instead of letting protected children render with me=null
      // (which would cause a /dashboard ↔ / redirect loop).
      setLoading(true);
      try {
        const res = await api.get<{ user: PortalUser }>('/api/me');
        setMe(res.user);
        setAccessDenied(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403 && err.code === 'ACCESS_DENIED') {
          setAccessDenied(true);
          setMe(null);
          await fbSignOut(auth).catch(() => {});
        } else {
          // Network or transient error — surface via me=null but keep the session.
          setMe(null);
        }
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { auth } = firebase();
    setAccessDenied(false);
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    const { auth } = firebase();
    setAccessDenied(false);
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && cred.user) await updateProfile(cred.user, { displayName });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { auth } = firebase();
    setAccessDenied(false);
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  }, []);

  const signOut = useCallback(async () => {
    const { auth } = firebase();
    await fbSignOut(auth);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { auth } = firebase();
    await sendPasswordResetEmail(auth, email);
  }, []);

  const getIdToken = useCallback(async () => {
    const { auth } = firebase();
    const u = auth.currentUser;
    if (!u) return null;
    return u.getIdToken();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      me,
      loading,
      accessDenied,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
      getIdToken,
    }),
    [user, me, loading, accessDenied, signIn, signUp, signInWithGoogle, signOut, resetPassword, getIdToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
